import { TableEmptyState } from "@admin-ui/components/composed/table-empty-state/table-empty-state";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Tab, TabList, Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { BadgeWithDot } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@admin-ui/components/primitives/base/tooltip/tooltip";
import { formatFullDateTime, formatRelativeTime } from "@admin-ui/utils/format-date";
import type AdminAccountsBadge from "@travily/types/models/AdminAccountsBadge";
import type AdminAccountsBadgeAward from "@travily/types/models/AdminAccountsBadgeAward";
import { Plus, Trash01, Trophy01 } from "@untitledui/icons";
import { type Key, useMemo, useState } from "react";
import { TableBody } from "react-aria-components";

export type BadgesFilterKey = "all" | "travel" | "platform" | "admin" | "system";

export interface BadgesPanelCopy {
  title: string;
  description: string;
  awardAction: string;
  revokeAction: string;
  emptyTitle: string;
  emptyDescription: string;
  stats: {
    total: string;
    travel: string;
    platform: string;
    adminGrants: string;
  };
  filters: Record<BadgesFilterKey, string>;
  source: { system: string; admin: string };
  columns: {
    badge: string;
    level: string;
    source: string;
    awarded: string;
    by: string;
  };
}

export interface BadgesPanelProps {
  awards: AdminAccountsBadgeAward[];
  availableBadges: AdminAccountsBadge[];
  onAwardOpen: () => void;
  onRevoke: (awardId: number) => void;
  copy: BadgesPanelCopy;
}

function formatLevel(award: AdminAccountsBadgeAward): string {
  return award.level > 1 ? String(award.level) : "—";
}

function awardedByLabel(award: AdminAccountsBadgeAward): string {
  if (!award.awarded_by) return "—";
  return award.awarded_by.name ?? award.awarded_by.email;
}

export function BadgesPanel({ awards, availableBadges, onAwardOpen, onRevoke, copy }: BadgesPanelProps) {
  const [filter, setFilter] = useState<BadgesFilterKey>("all");

  const categoryByKey = useMemo(
    () => new Map(availableBadges.map((badge) => [badge.key, badge.category])),
    [availableBadges],
  );

  const stats = useMemo(() => {
    let travel = 0;
    let platform = 0;
    let admin = 0;
    for (const award of awards) {
      const category = categoryByKey.get(award.key);
      if (category === "travel") travel += 1;
      else if (category === "platform") platform += 1;
      if (award.granted_by === "admin") admin += 1;
    }
    return { total: awards.length, travel, platform, admin };
  }, [awards, categoryByKey]);

  const filteredAwards = useMemo(() => {
    if (filter === "all") return awards;
    if (filter === "admin" || filter === "system") {
      return awards.filter((award) => award.granted_by === filter);
    }
    return awards.filter((award) => categoryByKey.get(award.key) === filter);
  }, [awards, filter, categoryByKey]);

  return (
    <div className="flex flex-col gap-6 pt-8">
      <div className="flex flex-wrap gap-2">
        <BadgeWithDot color="gray" size="md">
          {copy.stats.total} · {stats.total}
        </BadgeWithDot>
        <BadgeWithDot color="blue" size="md">
          {copy.stats.travel} · {stats.travel}
        </BadgeWithDot>
        <BadgeWithDot color="indigo" size="md">
          {copy.stats.platform} · {stats.platform}
        </BadgeWithDot>
        <BadgeWithDot color="warning" size="md">
          {copy.stats.adminGrants} · {stats.admin}
        </BadgeWithDot>
      </div>

      <TableCard.Root size="sm">
        <TableCard.Header
          title={copy.title}
          badge={String(awards.length)}
          description={copy.description}
          contentTrailing={
            <Button size="sm" color="primary" iconLeading={Plus} onClick={onAwardOpen}>
              {copy.awardAction}
            </Button>
          }
        />

        <div className="flex flex-wrap gap-2 border-b border-secondary bg-primary px-4 py-3 md:px-5">
          <Tabs selectedKey={filter} onSelectionChange={(key: Key) => setFilter(key as BadgesFilterKey)}>
            <TabList orientation="horizontal" type="button-minimal">
              <Tab id="all" label={copy.filters.all} />
              <Tab id="travel" label={copy.filters.travel} />
              <Tab id="platform" label={copy.filters.platform} />
              <Tab id="admin" label={copy.filters.admin} />
              <Tab id="system" label={copy.filters.system} />
            </TabList>
          </Tabs>
        </div>

        <Table aria-label="Awarded badges">
          <Table.Header>
            <Table.Head id="name" label={copy.columns.badge} isRowHeader className="w-full max-w-1/3" />
            <Table.Head id="level" label={copy.columns.level} />
            <Table.Head id="granted_by" label={copy.columns.source} />
            <Table.Head id="awarded_at" label={copy.columns.awarded} />
            <Table.Head id="awarded_by" label={copy.columns.by} />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody
            items={filteredAwards}
            renderEmptyState={() => (
              <TableEmptyState icon={Trophy01} title={copy.emptyTitle} description={copy.emptyDescription} />
            )}
          >
            {(award: AdminAccountsBadgeAward) => (
              <Table.Row key={award.id} id={award.id}>
                <Table.Cell>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-primary">{award.name}</span>
                    <code className="text-xs text-tertiary">{award.key}</code>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{formatLevel(award)}</span>
                </Table.Cell>
                <Table.Cell>
                  <BadgeWithDot color={award.granted_by === "admin" ? "warning" : "blue"} size="sm">
                    {copy.source[award.granted_by]}
                  </BadgeWithDot>
                </Table.Cell>
                <Table.Cell>
                  <Tooltip title={formatFullDateTime(award.awarded_at)} placement="top">
                    <TooltipTrigger className="cursor-default text-left text-sm text-tertiary">
                      {formatRelativeTime(award.awarded_at)}
                    </TooltipTrigger>
                  </Tooltip>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{awardedByLabel(award)}</span>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      size="sm"
                      color="link-color"
                      iconLeading={Trash01}
                      onClick={() => onRevoke(award.id)}
                      aria-label={`${copy.revokeAction} ${award.name}`}
                    >
                      {copy.revokeAction}
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>
    </div>
  );
}
